/** Public join link with room code prefilled on the home screen. */
export function buildJoinUrl(code: string): string {
  if (typeof window === "undefined") {
    return `?code=${encodeURIComponent(code)}`;
  }
  const url = new URL(window.location.href);
  url.search = "";
  url.hash = "";
  url.searchParams.set("code", code);
  return url.toString();
}

/**
 * Fun, paste-anywhere invite blurb. Room code + join link.
 * Safe for group chats, SMS, Slack, carrier pigeon.
 */
export function buildInviteText(code: string): string {
  const link = buildJoinUrl(code);
  return [
    "🎲 POPPYCOCK ALERT 🎲",
    "",
    "You've been summoned to a pure goofy party game.",
    "Ridiculous prompts. Anonymous answers, drawings, or captions.",
    "Name-chip guessing. Mild betrayal. Zero icebreakers.",
    "",
    `Room code: ${code}`,
    `Hop in: ${link}`,
    "",
    "Join anytime — even mid-game. We'll shove you in at the next open seat.",
    "No accounts. No apps. Just poppycock.",
  ].join("\n");
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

export async function copyInviteText(code: string): Promise<boolean> {
  return copyText(buildInviteText(code));
}

/** Just the join URL with room code — no blurb. */
export async function copyJoinUrl(code: string): Promise<boolean> {
  return copyText(buildJoinUrl(code));
}

export interface ScoreboardRow {
  name: string;
  score: number;
  isYou?: boolean;
}

const MODE_LABEL: Record<string, string> = {
  text: "All text",
  design: "All drawings",
  caption: "Caption contest",
  mix: "Mix",
};

/**
 * Wordle-style shareable results badge for group chats / social.
 * Plain text + emoji so it pastes cleanly everywhere.
 */
export function buildGameOverShareText(opts: {
  code: string;
  rounds: number;
  contentMode: string;
  standings: ScoreboardRow[];
  youName?: string;
}): string {
  const ranked = [...opts.standings].sort((a, b) => b.score - a.score);
  const medals = ["🥇", "🥈", "🥉"];
  const lines: string[] = [
    "🎲 Poppycock Results",
    `${opts.rounds} round${opts.rounds === 1 ? "" : "s"} · ${MODE_LABEL[opts.contentMode] ?? opts.contentMode}`,
    "",
  ];

  ranked.forEach((row, i) => {
    const medal = medals[i] ?? `${i + 1}.`;
    const you = row.isYou ? " ← me" : "";
    lines.push(`${medal} ${row.name}  ${row.score}${you}`);
  });

  const top = ranked[0];
  const yourRank = ranked.findIndex((r) => r.isYou);
  lines.push("");
  if (top && ranked.filter((r) => r.score === top.score).length > 1) {
    lines.push("It's a tie at the top. Chaos reigns.");
  } else if (yourRank === 0) {
    lines.push("I won. Bow before my nonsense. 👑");
  } else if (yourRank > 0) {
    lines.push(`I placed #${yourRank + 1}. The people were wrong.`);
  } else {
    lines.push("Pure goofy. Zero icebreakers.");
  }
  lines.push("");
  lines.push(`Room ${opts.code} · poppycock`);

  return lines.join("\n");
}

export async function copyGameOverShare(
  opts: Parameters<typeof buildGameOverShareText>[0],
): Promise<boolean> {
  return copyText(buildGameOverShareText(opts));
}
